import { FC, useState, useCallback } from 'react'
import { Button, Spinner, Typography } from '@material-tailwind/react'
import { useAgamiIndoorAudioFiles } from '../hooks/useAgamiIndoorAudioFiles'
import AgamiIndoorAudioFilesTable from './AgamiIndoorAudioFilesTable'
import MediaFileDropzone from './MediaFileDropzone'
import { uploadFile } from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'
import { useSessionContext } from '../contexts/SessionContext'
import { AGAMI_INDOOR_BUCKET } from '../utils/constants/db.constants'

interface AgamiIndoorAudioFilesManagerProps {
  uploadSizeLimit: number
  allowedFileTypes: string[]
}

const AgamiIndoorAudioFilesManager: FC<AgamiIndoorAudioFilesManagerProps> = ({
  uploadSizeLimit,
  allowedFileTypes,
}) => {
  const toast = useGeneralToast()
  const { user } = useSessionContext()
  const { audioFiles, isLoading, refetch } = useAgamiIndoorAudioFiles()
  const [newAudioFile, setNewAudioFile] = useState<File | undefined>(undefined)
  const [uploadingFile, setUploadingFile] = useState(false)

  const uploadAudioFile = useCallback(async () => {
    if (!newAudioFile || !user) return
    setUploadingFile(true)
    try {
      const { error } = await uploadFile({
        filename: `${user.id}/audio/${newAudioFile?.name}`,
        bucketId: AGAMI_INDOOR_BUCKET,
        file: newAudioFile,
        cacheControl: '0',
        contentType: newAudioFile.type,
        upsert: true,
      })

      if (error) {
        toast.error('Something went wrong')
        return
      }

      refetch()
      toast.success('File uploaded!')
      setNewAudioFile(undefined)
    } catch {
      // Upload error
    } finally {
      setUploadingFile(false)
    }
  }, [newAudioFile, user, refetch])

  return (
    <div>
      <div className="flex gap-2 mb-2 items-center">
        <Typography variant="h3" className="mb-2">
          Audio files
        </Typography>
        {newAudioFile && (
          <Button size="sm" disabled={uploadingFile} onClick={uploadAudioFile}>
            {uploadingFile ? <Spinner className="h-4 w-4" /> : 'UPLOAD FILE'}
          </Button>
        )}
      </div>

      <div className="mb-4 flex flex-col gap-3">
        <MediaFileDropzone
          editMode={false}
          mediaType={1} // audio
          uploadSizeLimit={uploadSizeLimit}
          allowedFileTypes={allowedFileTypes}
          mediaFile={newAudioFile}
          setMediaFile={(file) => setNewAudioFile(file || undefined)}
        />
      </div>

      {isLoading && <Spinner className="h-6 w-6" />}

      {!isLoading && !audioFiles && (
        <Typography className="text-center">
          <em> Error retrieving audio files</em>
        </Typography>
      )}

      {!isLoading && audioFiles?.length > 0 && (
        <AgamiIndoorAudioFilesTable audioFiles={audioFiles} refetch={refetch} />
      )}
    </div>
  )
}

export default AgamiIndoorAudioFilesManager
